import { defineStore } from 'pinia'

import contentfulResolveResponse from 'contentful-resolve-response'

import { featureFlags } from '@/lib/featureFlags'
import Sunny16CommerceContentService from '@/services/Sunny16CommerceContentService'

import { CadyMarket } from '@/types/CadyMarket'
import type { DisclaimerContent } from '@/types/DisclaimerContent'

import { FooterNavItem, HeaderNavItem } from '@VelocitySquared/cady-design-system'

import type { ContentfulContentModule } from '#contentful/ContentfulContentModule'
import type { ContentfulContentPageAlpha } from '#contentful/ContentfulContentPageAlpha'

// Identifiers of the four Senior-ordering content modules, loaded when the
// market resolves to Senior (each flag-backed module only when its feature is
// enabled — see `loadSeniorOrderingContent`). Keep these in sync with the
// per-module content-model guides (`-002`…`-005`).
const SENIOR_ORDERING_MODULE_IDENTIFIERS = {
  missingCapGown: 'senior-gallery-missing-capgown-modal',
  portfolioTracker: 'senior-portfolio-tracker',
  packageBuilder: 'senior-package-builder',
  yearbookPage: 'senior-yearbook-page'
} as const

// Identifier of the universal Rewards Tracker content module (heading templates).
// Ungated — the tracker is content-driven across all markets (ADR 003), so this
// loads once per session regardless of market, unlike the Senior modules above.
const REWARDS_TRACKER_MODULE_IDENTIFIER = 'rewards-tracker'

export interface GlobalContentNavItem {
  label?: string
  url?: string
}

interface AppContentStoreState {
  globalAppContent: {
    nameInternal?: string
    identifier?: string
    contentItems?: Array<{
      sys: { type: string }
      fields?: { identifier: string; value: string }
    }>
  } | null
  marketPageContent: ContentfulContentPageAlpha | null
  disclaimerOverride: DisclaimerContent | null
  // Senior-ordering content modules — loaded once per session when the market
  // resolves to Senior, read by their components with the local `content.ts`
  // object as fallback. See ADR 2026.06.08-004.
  seniorMissingCapGownContent: ContentfulContentModule | null
  seniorPortfolioTrackerContent: ContentfulContentModule | null
  seniorPackageBuilderContent: ContentfulContentModule | null
  seniorYearbookPageContent: ContentfulContentModule | null
  seniorOrderingContentLoaded: boolean
  // Universal Rewards Tracker content module (heading templates) — loaded once
  // per session, ungated by market. Read via the `useRewards` data layer.
  rewardsTrackerContent: ContentfulContentModule | null
  rewardsTrackerContentLoaded: boolean
  // Checkout page content module — loaded by CheckoutView on mount,
  // read directly by checkout components without prop threading.
  checkoutPageContent: ContentfulContentModule | null
}

// Helper function to parse nav bar content from globalAppContent
function parseNavBarContent(
  globalAppContent: AppContentStoreState['globalAppContent'],
  pattern: RegExp
): GlobalContentNavItem[] {
  const navItemsMap = new Map<number, GlobalContentNavItem>()

  if (!globalAppContent?.contentItems) {
    return []
  }

  globalAppContent.contentItems.forEach((contentItem) => {
    if (contentItem.sys.type === 'Link') {
      return
    }

    const match = contentItem.fields?.identifier.match(pattern)
    if (!match) return

    const itemNumber = parseInt(match[1], 10)
    const type = match[2]

    if (!navItemsMap.has(itemNumber)) {
      navItemsMap.set(itemNumber, {})
    }
    const navItem = navItemsMap.get(itemNumber)!

    if (type === 'label') {
      navItem.label = contentItem.fields?.value
    } else if (type === 'link') {
      navItem.url = contentItem.fields?.value
    }
  })

  const navItems: GlobalContentNavItem[] = []
  const sortedKeys = Array.from(navItemsMap.keys()).sort((a, b) => a - b)
  sortedKeys.forEach((key) => {
    const navItem = navItemsMap.get(key)!
    // Only render items configured with both a label and a link. A partially
    // configured content entry (one half missing) would otherwise render an
    // empty or hrefless link, so it's dropped here. Applies to both the footer
    // and top nav, which share this parser.
    if (navItem.label && navItem.url) {
      navItems.push(navItem)
    }
  })

  return navItems
}

// The "Order Photos" market children, in design order (Figma: SY26-27 Global
// Ordering nav). Structure lives in code — the content team edits only the item
// text/links via the self-describing identifiers below (BugHerd #64). Fallback
// URLs mirror the live home.cady.com "Order Photos" menu, which links to
// store.cady.com — a separate site from this app (shop.cady.com), so these are
// external and open in a new tab. They're the real production destinations until
// the Contentful entries are authored. (store.cady.com has its own routes; don't
// expect them to match this app's router.)
const MARKET_NAV_ITEMS: Array<{
  market: CadyMarket
  labelId: string
  linkId: string
  fallbackLabel: string
  fallbackUrl: string
}> = [
  {
    market: CadyMarket.Senior,
    labelId: 'top-nav-seniors-label',
    linkId: 'top-nav-seniors-link',
    fallbackLabel: 'Seniors Portraits',
    fallbackUrl: 'https://store.cady.com/dashboard'
  },
  {
    market: CadyMarket.Underclass,
    labelId: 'top-nav-underclass-label',
    linkId: 'top-nav-underclass-link',
    fallbackLabel: 'Underclassmen Photos',
    fallbackUrl: 'https://store.cady.com/underclass'
  },
  {
    market: CadyMarket.CapGown,
    labelId: 'top-nav-capgown-label',
    linkId: 'top-nav-capgown-link',
    fallbackLabel: 'Cap & Gown Images',
    fallbackUrl: 'https://store.cady.com/capandgown'
  },
  {
    market: CadyMarket.Graduation,
    labelId: 'top-nav-graduation-label',
    linkId: 'top-nav-graduation-link',
    fallbackLabel: 'Graduation Ceremony',
    fallbackUrl: 'https://store.cady.com/graduation'
  },
  {
    market: CadyMarket.Athletic,
    labelId: 'top-nav-athletics-label',
    linkId: 'top-nav-athletics-link',
    fallbackLabel: 'Athletic Images',
    fallbackUrl: 'https://store.cady.com/athletics'
  }
]

// A nav link opens in a new tab only when it points to a *different site*. A URL
// is external when it's absolute AND on a different origin than this app
// (shop.cady.com) — so relative routes (e.g. `/senior`) and any same-origin
// absolute link stay in the same tab, while other sites (store.cady.com,
// home.cady.com, the FAQ host) open in a new tab. The design-system header is
// intentionally agnostic about this; the storefront decides per link.
function isExternalUrl(url: string): boolean {
  if (!/^(https?:)?\/\//i.test(url)) {
    return false
  }
  if (typeof window === 'undefined') {
    return true
  }
  try {
    return new URL(url, window.location.href).origin !== window.location.origin
  } catch {
    return true
  }
}

export const useAppContentStore = defineStore('appContent', {
  state: (): AppContentStoreState => ({
    globalAppContent: null,
    marketPageContent: null,
    disclaimerOverride: null,
    seniorMissingCapGownContent: null,
    seniorPortfolioTrackerContent: null,
    seniorPackageBuilderContent: null,
    seniorYearbookPageContent: null,
    seniorOrderingContentLoaded: false,
    rewardsTrackerContent: null,
    rewardsTrackerContentLoaded: false,
    checkoutPageContent: null
  }),
  getters: {
    footerNavContent(state): FooterNavItem[] {
      return parseNavBarContent(
        state.globalAppContent,
        /^footer-nav-item-(\d+)-(label|link)$/
      ) as FooterNavItem[]
    },
    contentItemValue(state) {
      return (identifier: string): string | undefined => {
        if (!state.globalAppContent?.contentItems) {
          return undefined
        }
        const contentItem = state.globalAppContent.contentItems.find(
          (item) => item.fields?.identifier === identifier
        )
        return contentItem?.fields?.value
      }
    },
    // The hierarchical top nav (BugHerd #64): "Book a Session", an "Order Photos"
    // grouping parent (the markets), and "FAQ". Structure is fixed in code; each
    // label/link resolves from a self-describing content identifier, falling back
    // to a code default. Returns a function taking the current market so the store
    // stays decoupled from the main store — the caller passes it in and the active
    // child is highlighted by the design-system header.
    mainNavTree() {
      return (activeMarket: CadyMarket | null): HeaderNavItem[] => {
        const text = (identifier: string, fallback: string): string =>
          this.contentItemValue(identifier) ?? fallback
        // Resolve a link's url from content (with fallback) and flag off-site
        // links to open in a new tab.
        const link = (identifier: string, fallback: string): { url: string; newTab: boolean } => {
          const url = text(identifier, fallback)
          return { url, newTab: isExternalUrl(url) }
        }

        return [
          {
            label: text('top-nav-book-session-label', 'Book a Session'),
            ...link('top-nav-book-session-link', 'https://booking.cady.com/')
          },
          {
            // Grouping parent — no link of its own; expands to the market children.
            label: text('top-nav-order-photos-label', 'Order Photos'),
            children: MARKET_NAV_ITEMS.map((item) => ({
              label: text(item.labelId, item.fallbackLabel),
              ...link(item.linkId, item.fallbackUrl),
              active: activeMarket === item.market
            }))
          },
          {
            label: text('top-nav-faq-label', 'FAQ'),
            ...link('top-nav-faq-link', 'https://cadystudios.zendesk.com/hc/en-us')
          }
        ]
      }
    }
  },
  actions: {
    setGlobalAppContent(content: AppContentStoreState['globalAppContent']) {
      this.globalAppContent = content
    },
    setMarketPageContent(content: ContentfulContentPageAlpha | null) {
      this.marketPageContent = content
    },
    setDisclaimerOverride(content: DisclaimerContent | null) {
      this.disclaimerOverride = content
    },
    // Fetches the Senior-ordering content modules in parallel and caches them.
    // Idempotent — the guard flag prevents concurrent/repeat fetches within a
    // session. Each fetch is isolated: a failed module resolves to `null`,
    // leaving its component on the `content.ts` fallback rather than failing
    // the whole load.
    //
    // Only modules whose feature is enabled are fetched: Portfolio Tracker,
    // Package Builder, and Yearbook each gate on their feature flag, so a
    // disabled feature pays nothing and its module stays `null`. Missing Cap &
    // Gown has no flag — it's part of the Senior gallery baseline — so it
    // always loads.
    //
    // The guard is claimed up front and is deliberately sticky: the watcher
    // fires roughly once per session, and a not-yet-authored entry returns a
    // 404 that's expected during rollout, so we don't release the guard to
    // retry (that would just re-hammer the API for entries that are absent by
    // design — the `content.ts` fallback is the intended steady state until
    // the entries are promoted).
    async loadSeniorOrderingContent(): Promise<void> {
      if (this.seniorOrderingContentLoaded) {
        return
      }
      this.seniorOrderingContentLoaded = true

      const contentService = new Sunny16CommerceContentService()
      const fetchModule = async (identifier: string): Promise<ContentfulContentModule | null> => {
        try {
          const response = await contentService.getArbitraryContent('contentModule', identifier)
          return (contentfulResolveResponse(response)[0]?.fields as ContentfulContentModule) ?? null
        } catch {
          return null
        }
      }
      const fetchIfEnabled = (
        enabled: boolean,
        identifier: string
      ): Promise<ContentfulContentModule | null> =>
        enabled ? fetchModule(identifier) : Promise.resolve(null)

      const [missingCapGown, portfolioTracker, packageBuilder, yearbookPage] = await Promise.all([
        fetchModule(SENIOR_ORDERING_MODULE_IDENTIFIERS.missingCapGown),
        fetchIfEnabled(
          featureFlags.portfolioTracker,
          SENIOR_ORDERING_MODULE_IDENTIFIERS.portfolioTracker
        ),
        fetchIfEnabled(
          featureFlags.packageBuilder,
          SENIOR_ORDERING_MODULE_IDENTIFIERS.packageBuilder
        ),
        fetchIfEnabled(
          featureFlags.yearbookSelection,
          SENIOR_ORDERING_MODULE_IDENTIFIERS.yearbookPage
        )
      ])

      // If `resetStore()` ran while the fetches were in flight, the guard was
      // cleared — drop these now-stale results rather than repopulating the
      // freshly-reset store.
      if (!this.seniorOrderingContentLoaded) {
        return
      }

      this.seniorMissingCapGownContent = missingCapGown
      this.seniorPortfolioTrackerContent = portfolioTracker
      this.seniorPackageBuilderContent = packageBuilder
      this.seniorYearbookPageContent = yearbookPage
    },
    // Fetches the universal Rewards Tracker content module (heading templates).
    // Ungated by market and idempotent via the guard. A failed/absent module
    // resolves to `null`, leaving headings on their in-code fallbacks. Mirrors
    // the Senior loader's sticky-guard rationale: an absent entry during rollout
    // is expected, so we don't release the guard to retry.
    async loadRewardsTrackerContent(): Promise<void> {
      if (this.rewardsTrackerContentLoaded) {
        return
      }
      this.rewardsTrackerContentLoaded = true

      const contentService = new Sunny16CommerceContentService()
      let module: ContentfulContentModule | null = null
      try {
        const response = await contentService.getArbitraryContent(
          'contentModule',
          REWARDS_TRACKER_MODULE_IDENTIFIER
        )
        module = (contentfulResolveResponse(response)[0]?.fields as ContentfulContentModule) ?? null
      } catch {
        module = null
      }

      // Drop a now-stale result if `resetStore()` ran while the fetch was in flight.
      if (!this.rewardsTrackerContentLoaded) {
        return
      }
      this.rewardsTrackerContent = module
    },
    setCheckoutPageContent(content: ContentfulContentModule | null) {
      this.checkoutPageContent = content
    },
    resetStore(): void {
      this.globalAppContent = null
      this.marketPageContent = null
      this.disclaimerOverride = null
      this.seniorMissingCapGownContent = null
      this.seniorPortfolioTrackerContent = null
      this.seniorPackageBuilderContent = null
      this.seniorYearbookPageContent = null
      this.seniorOrderingContentLoaded = false
      this.rewardsTrackerContent = null
      this.rewardsTrackerContentLoaded = false
      this.checkoutPageContent = null
    }
  },
  persist: {
    // Senior-ordering modules + their load guard are session-scoped (re-fetched
    // each session via the App.vue watcher), so they stay out of persistence.
    omit: [
      'disclaimerOverride',
      'seniorMissingCapGownContent',
      'seniorPortfolioTrackerContent',
      'seniorPackageBuilderContent',
      'seniorYearbookPageContent',
      'seniorOrderingContentLoaded',
      'rewardsTrackerContent',
      'rewardsTrackerContentLoaded',
      'checkoutPageContent'
    ]
  }
})
